import React, { useState, useMemo, useCallback } from 'react';
import { 
  Search, HelpCircle, ChevronDown, ChevronRight, 
  MessageCircle, Mail, Shield, CreditCard, Users, 
  Settings, Monitor, BarChart3, Globe, Bot
} from 'lucide-react';
import { api } from '../../services';

const AgentSupport = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [currentQueryId, setCurrentQueryId] = useState(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState({ isAvailable: false });
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [contactFormSubmitting, setContactFormSubmitting] = useState(false);
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const [dynamicKnowledgeBase, setDynamicKnowledgeBase] = useState({});
  const [kbLoading, setKbLoading] = useState(true);

  // API functions for logging queries and feedback
  const logQuery = useCallback(async (queryText, resultsFound, categoryClicked = null) => {
    try {
      const response = await api.post('/agent-support/log-query', {
        query_text: queryText,
        results_found: resultsFound,
        category_clicked: categoryClicked,
        session_id: sessionId
      });
      
      if (response.data.success) {
        setCurrentQueryId(response.data.queryId);
        return response.data.queryId;
      }
    } catch (error) {
      console.error('Error logging query:', error);
    }
    return null;
  }, [sessionId]);

  const logFeedback = useCallback(async (queryId, feedbackType) => {
    try {
      await api.post('/agent-support/log-feedback', {
        query_id: queryId,
        feedback_given: feedbackType
      });
    } catch (error) {
      console.error('Error logging feedback:', error);
    }
  }, []);

  // AI Assistant functions
  const checkAIStatus = useCallback(async () => {
    try {
      const response = await api.get('/agent-support/ai-status');
      if (response.data.success) {
        setAiStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error checking AI status:', error);
      setAiStatus({ isAvailable: false });
    }
  }, []);

  const askAI = useCallback(async (query) => {
    if (!query.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await api.post('/agent-support/ai-query', {
        query_text: query.trim(),
        session_id: sessionId,
        include_system_data: false
      });
      
      if (response.data.success) {
        setAiResponse({
          ...response.data.data,
          query: query.trim(),
          timestamp: new Date()
        });
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      setAiResponse({
        error: true,
        message: error.response?.data?.error || 'Failed to get AI response',
        fallback: error.response?.data?.fallback
      });
    } finally {
      setAiLoading(false);
    }
  }, [sessionId]);

  const submitAIFeedback = useCallback(async (conversationId, rating) => {
    try {
      await api.post('/agent-support/ai-feedback', {
        conversation_id: conversationId,
        feedback_rating: rating
      });
    } catch (error) {
      console.error('Error submitting AI feedback:', error);
    }
  }, []);

  // Fetch dynamic knowledge base articles from database
  const fetchDynamicKnowledgeBase = useCallback(async () => {
    try {
      setKbLoading(true);
      const response = await api.get('/agent-support/knowledge-base');
      
      if (response.data.success) {
        setDynamicKnowledgeBase(response.data.data.articles);
      }
    } catch (error) {
      console.error('Error fetching dynamic knowledge base:', error);
    } finally {
      setKbLoading(false);
    }
  }, []);

  // Check AI status and load KB on component mount
  React.useEffect(() => {
    checkAIStatus();
    fetchDynamicKnowledgeBase();
  }, [checkAIStatus, fetchDynamicKnowledgeBase]);

  // Contact support form functions
  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check for commission-related keywords
  const checkForCommissionKeywords = useCallback((subject, message) => {
    const commissionKeywords = [
      'commission', 'commissions', 'payment', 'paid', 'unpaid', 'amount', 
      'reservation', 'booking', 'duplicate', 'correction', 'wrong', 'update',
      'money', 'owed', 'missing', 'error', 'fix', 'change'
    ];
    
    const text = `${subject} ${message}`.toLowerCase();
    return commissionKeywords.some(keyword => text.includes(keyword));
  }, []);

  const submitContactForm = useCallback(async () => {
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      alert('Please fill in both subject and message fields.');
      return;
    }

    setContactFormSubmitting(true);
    try {
      const response = await api.post('/agent-support/contact-support', {
        subject: contactForm.subject.trim(),
        message: contactForm.message.trim(),
        priority: contactForm.priority,
        session_id: sessionId
      });

      if (response.data.success) {
        setContactFormSubmitted(true);
        setContactForm({ subject: '', message: '', priority: 'normal' });
        // Auto-close form after 3 seconds
        setTimeout(() => {
          setShowContactForm(false);
          setContactFormSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('Failed to submit support request. Please try again.');
    } finally {
      setContactFormSubmitting(false);
    }
  }, [contactForm, sessionId]);

  const closeContactForm = () => {
    setShowContactForm(false);
    setContactFormSubmitted(false);
    setContactForm({ subject: '', message: '', priority: 'normal' });
  };

  // Knowledge base content organized by categories
  const knowledgeBase = {
    authentication: {
      title: "Account & Authentication",
      icon: Shield,
      color: "text-red-600",
      bgColor: "bg-red-50",
      items: [
        {
          id: 'auth-1',
          question: "Invalid Setup Link. This password setup link is invalid or has expired.",
          answer: "Password setup links expire after 72 hours for security reasons. Contact your agency administrator to resend a new setup link. The new link will be valid for another 72 hours.",
          keywords: ["invalid", "setup", "link", "expired", "password"]
        },
        {
          id: 'auth-2',
          question: "I can't log in - it keeps redirecting me back to the login page",
          answer: "This usually indicates a browser cache issue or incorrect credentials. Try these steps: 1) Clear your browser cache and cookies, 2) Ensure you're using the correct email and password, 3) Try using an incognito/private browser window, 4) If issues persist, contact your agency administrator.",
          keywords: ["login", "redirect", "cache", "credentials"]
        },
        {
          id: 'auth-3',
          question: "I forgot my password, how do I reset it?",
          answer: "Use the 'Forgot Password' link on the login page to request a password reset, or contact your agency administrator directly. They can send you a new password setup link that will allow you to create a new password.",
          keywords: ["forgot", "password", "reset"]
        },
        {
          id: 'auth-4',
          question: "How long are password setup links valid?",
          answer: "Password setup links are valid for 72 hours (3 days) from the time they are generated. After expiration, you'll need to request a new link from your agency administrator.",
          keywords: ["password", "setup", "link", "valid", "72", "hours"]
        }
      ]
    },
    commissions: {
      title: "Commission Management",
      icon: CreditCard,
      color: "text-green-600",
      bgColor: "bg-green-50",
      items: [
        {
          id: 'comm-0',
          question: "How do I correct or ask questions about a specific commission?",
          answer: "**Use the messaging feature on your commission directly** - go to Commissions → find your commission → click 'Message'. This goes directly to your admin and keeps all communication tied to that specific commission.\n\n**DO NOT use support tickets for commission corrections** - support tickets are only for when the commission system itself isn't working (errors, bugs, technical issues).",
          keywords: ["commission", "correct", "question", "message", "support", "ticket", "how to"]
        },
        {
          id: 'comm-1',
          question: "My commission status became blank/grey after sending a message",
          answer: "This issue has been resolved. Commission status now remains unchanged when sending messages. The message button will turn red when a reply is needed from you. If you're still experiencing this issue, refresh the page and try again.",
          keywords: ["commission", "status", "blank", "grey", "message"]
        },
        {
          id: 'comm-2',
          question: "I submitted a commission but didn't receive a confirmation email",
          answer: "Confirmation emails are now automatically sent when you submit a commission request. Check your spam/junk folder first. If you still don't see the confirmation email, contact support as there may be an email delivery issue.",
          keywords: ["commission", "confirmation", "email", "submitted"]
        },
        {
          id: 'comm-3',
          question: "How do I know if my commission is a duplicate?",
          answer: "Duplicate commissions are automatically detected and highlighted with a red background and 'DUPLICATE' badge in the commission list. The system checks for similar reservation numbers, including partial matches (e.g., '73022051557625' and '73022051557625 (adjustment)').",
          keywords: ["duplicate", "commission", "reservation", "number"]
        },
        {
          id: 'comm-4',
          question: "What does each commission status mean?",
          answer: "Commission statuses: **Pending** - Submitted, awaiting admin review; **Approved** - Approved for payment processing; **On Hold** - Needs additional information or clarification; **Denied** - Not approved for payment; **Paid** - Payment has been processed and completed.",
          keywords: ["commission", "status", "pending", "approved", "denied", "paid", "hold"]
        },
        {
          id: 'comm-5',
          question: "How do I edit or update a submitted commission?",
          answer: "Once submitted, commissions cannot be directly edited by agents. If you need to make changes, use the messaging feature to communicate with your administrator about the required updates. They can make the necessary changes or guide you on resubmitting if needed.",
          keywords: ["edit", "update", "commission", "submitted"]
        }
      ]
    },
    email: {
      title: "Email & Communication",
      icon: Mail,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      items: [
        {
          id: 'email-1',
          question: "I'm not receiving emails from the system",
          answer: "First, check your spam/junk folder as system emails may be filtered. Ensure your email address is correct in your profile settings. If emails continue to be missing, contact your agency administrator to verify your email address in the system.",
          keywords: ["email", "not receiving", "spam", "junk"]
        },
        {
          id: 'email-2',
          question: "How long does it take to receive password setup emails?",
          answer: "Password setup emails are sent immediately when requested by your administrator. If you don't receive it within 5 minutes, check your spam folder. The system has email rate limiting, so during busy periods there may be slight delays.",
          keywords: ["password", "setup", "email", "time", "delay"]
        },
        {
          id: 'email-3',
          question: "Can I change my email address?",
          answer: "Contact your agency administrator to update your email address. This ensures all system records are properly updated across multiple tables and you continue to receive important notifications.",
          keywords: ["change", "email", "address", "update"]
        }
      ]
    },
    quotes: {
      title: "Quote & Itinerary Management",
      icon: MessageCircle,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      items: [
        {
          id: 'quote-1',
          question: "I get a 500 error when converting a quote to an itinerary",
          answer: "This was a known issue related to foreign key constraints that has been resolved. If you still encounter this error, refresh the page and try the conversion again. Contact support if the issue persists.",
          keywords: ["500", "error", "quote", "itinerary", "convert"]
        },
        {
          id: 'quote-2',
          question: "How do I add travel components to a quote?",
          answer: "Use the 'Add Component' button in the quote builder and select from available options: flights, hotels, car rentals, cruises, restaurants, theme parks, etc. Fill in all required fields before saving each component.",
          keywords: ["add", "component", "quote", "travel", "flight", "hotel"]
        },
        {
          id: 'quote-3',
          question: "Can I edit a quote after it's been sent to a client?",
          answer: "Yes, you can edit quotes even after they've been sent. However, it's good practice to notify your client of any changes and consider resending the updated quote to ensure they have the latest information.",
          keywords: ["edit", "quote", "sent", "client", "update"]
        }
      ]
    },
    clients: {
      title: "Client Management",
      icon: Users,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      items: [
        {
          id: 'client-1',
          question: "How do I add a new client?",
          answer: "Go to Clients → Add New Client. Fill in the lead traveler information including name, email, phone, and address. You can also add additional travelers if needed. All required fields must be completed before saving.",
          keywords: ["add", "new", "client", "lead", "traveler"]
        },
        {
          id: 'client-2',
          question: "Can I merge duplicate client records?",
          answer: "Contact your agency administrator to merge duplicate client records. This ensures data integrity and prevents issues with quotes, itineraries, and commission tracking.",
          keywords: ["merge", "duplicate", "client", "records"]
        },
        {
          id: 'client-3',
          question: "How do I update client information?",
          answer: "Navigate to the client's profile and use the edit function to update their information. Changes will be reflected across all related quotes and itineraries.",
          keywords: ["update", "client", "information", "edit"]
        }
      ]
    },
    profile: {
      title: "Profile & Settings",
      icon: Settings,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      items: [
        {
          id: 'profile-1',
          question: "How do I update my commission percentage?",
          answer: "Only agency administrators can update commission percentages. Contact your admin to request changes to your commission rate. This ensures proper approval and system-wide consistency.",
          keywords: ["commission", "percentage", "update", "rate"]
        },
        {
          id: 'profile-2',
          question: "What is Elite status and how do I get it?",
          answer: "Elite status is a designation assigned by agency administrators based on performance criteria such as sales volume, client satisfaction, or other metrics. Contact your administrator for information about Elite agent requirements and how to achieve this status.",
          keywords: ["elite", "status", "performance", "criteria"]
        },
        {
          id: 'profile-3',
          question: "How do I update my profile information?",
          answer: "You can update basic profile information like your name and phone number in your profile settings. For email address changes or commission percentage updates, contact your agency administrator.",
          keywords: ["profile", "update", "information", "settings"]
        }
      ]
    },
    technical: {
      title: "Technical Issues",
      icon: Monitor,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      items: [
        {
          id: 'tech-1',
          question: "The page won't load or shows a blank screen",
          answer: "Try these troubleshooting steps: 1) Refresh the page (Ctrl+F5 or Cmd+Shift+R), 2) Clear your browser cache and cookies, 3) Try a different browser, 4) Check your internet connection, 5) Contact support if issues persist.",
          keywords: ["page", "load", "blank", "screen", "refresh"]
        },
        {
          id: 'tech-2',
          question: "I get 'Access denied' errors",
          answer: "Ensure you're logged in with the correct account and have the necessary permissions. Some features require specific access levels. If you believe you should have access to a feature, contact your agency administrator.",
          keywords: ["access", "denied", "permissions", "login"]
        },
        {
          id: 'tech-3',
          question: "Upload buttons don't work in modal windows",
          answer: "This was a known issue that has been fixed. If you're still experiencing problems: 1) Refresh the page and try again, 2) Ensure your browser allows file uploads, 3) Check that the file size is within limits, 4) Try a different browser if issues persist.",
          keywords: ["upload", "button", "modal", "window", "file"]
        }
      ]
    },
    reporting: {
      title: "Reporting & Analytics",
      icon: BarChart3,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      items: [
        {
          id: 'report-1',
          question: "Why don't I see all my commissions in the list?",
          answer: "The commission list uses pagination and filtering. Use the search box to find specific commissions, apply date filters, or scroll down to load more results. The system loads 50 commissions at a time for better performance.",
          keywords: ["commission", "list", "pagination", "filter", "search"]
        },
        {
          id: 'report-2',
          question: "How do I download my commission reports?",
          answer: "Use the export functionality in the Commissions section. You can download reports as CSV or PDF files. Use the date filters to specify the time period for your report.",
          keywords: ["download", "commission", "report", "export", "csv", "pdf"]
        },
        {
          id: 'report-3',
          question: "How do I view my performance analytics?",
          answer: "Access your dashboard to view performance metrics including total sales, commission earnings, client count, and quote conversion rates. Use the date range selector to view different time periods.",
          keywords: ["performance", "analytics", "dashboard", "metrics", "sales"]
        }
      ]
    },
    website: {
      title: "Website & Subdomain",
      icon: Globe,
      color: "text-pink-600",
      bgColor: "bg-pink-50",
      items: [
        {
          id: 'web-1',
          question: "My agent website isn't working",
          answer: "Verify your subdomain is correctly configured (yourname.tpcrm.io). Check that your agent website settings are properly set up in your profile. Contact support if your agent website isn't accessible or displaying correctly.",
          keywords: ["agent", "website", "subdomain", "not working"]
        },
        {
          id: 'web-2',
          question: "Contact forms on my website aren't working",
          answer: "This was a known issue that has been resolved. Contact forms now properly submit to the main system. If you're still experiencing issues, clear your browser cache and test the form again.",
          keywords: ["contact", "form", "website", "not working", "submit"]
        },
        {
          id: 'web-3',
          question: "How do I customize my agent website?",
          answer: "Access your website settings in your profile to customize your agent website. You can update your bio, contact information, and theme preferences. Changes may take a few minutes to appear on your live site.",
          keywords: ["customize", "agent", "website", "settings", "theme"]
        }
      ]
    },
    tax_forms: {
      title: "Tax Forms & Documentation",
      icon: HelpCircle,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      items: [
        {
          id: 'tax-1',
          question: "I need to change from W-9 to W-8BEN form but the system won't let me",
          answer: "This usually happens when your country setting is incorrect. W-9 is for US persons, W-8BEN is for non-US persons. Contact support to update your country setting, or access the Tax Forms section and click 'Fill New W-8BEN' to submit the correct form manually.",
          keywords: ["w-9", "w-8ben", "tax form", "country", "change", "switch", "mexico", "non-us"]
        },
        {
          id: 'tax-2',
          question: "What's the difference between W-9 and W-8BEN forms?",
          answer: "W-9 is for US citizens and residents, including Puerto Rico (US persons). W-8BEN is for non-US individuals (foreign persons). The system automatically selects the correct form based on your country setting.",
          keywords: ["w-9", "w-8ben", "difference", "us person", "non-us", "foreign", "citizen"]
        },
        {
          id: 'tax-3',
          question: "How do I submit a tax form?",
          answer: "Go to the Tax Forms section in your dashboard. The system will show the appropriate form based on your country. Fill out all required fields and submit. You can also manually select 'Fill New W-9' or 'Fill New W-8BEN' if needed.",
          keywords: ["submit", "tax form", "dashboard", "fill", "complete"]
        },
        {
          id: 'tax-4',
          question: "Can I submit multiple tax forms or update my form?",
          answer: "Yes, you can submit updated versions of your tax forms. Each submission creates a new version, and the latest version becomes your active form. Previous versions remain on file for record-keeping.",
          keywords: ["multiple", "update", "version", "new form", "change"]
        }
      ]
    }
  };

  // Toggle category expansion
  const toggleCategory = async (categoryKey) => {
    const wasExpanded = expandedCategories[categoryKey];
    
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
    
    // Log category click if expanding (not collapsing)
    if (!wasExpanded) {
      const categoryTitle = mergedKnowledgeBase[categoryKey]?.title || categoryKey;
      await logQuery(`Category: ${categoryTitle}`, true, categoryKey);
    }
  };

  // Merge static and dynamic knowledge base
  const mergedKnowledgeBase = useMemo(() => {
    const merged = { ...knowledgeBase };
    
    // Add dynamic articles from database to existing categories
    Object.entries(dynamicKnowledgeBase).forEach(([categoryKey, articles]) => {
      if (merged[categoryKey]) {
        // Add to existing category
        const dynamicItems = articles.map(article => ({
          id: article.article_id,
          question: article.question,
          answer: article.answer,
          keywords: article.keywords || [],
          isDynamic: true,
          views: article.views_count,
          helpful: article.helpful_votes
        }));
        merged[categoryKey].items = [...merged[categoryKey].items, ...dynamicItems];
      } else {
        // Create new category for dynamic articles
        merged[categoryKey] = {
          title: categoryKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: HelpCircle,
          color: "text-gray-600",
          bgColor: "bg-gray-50",
          items: articles.map(article => ({
            id: article.article_id,
            question: article.question,
            answer: article.answer,
            keywords: article.keywords || [],
            isDynamic: true,
            views: article.views_count,
            helpful: article.helpful_votes
          }))
        };
      }
    });
    
    return merged;
  }, [dynamicKnowledgeBase]);

  // Filter items based on search term
  const filteredResults = useMemo(() => {
    if (!searchTerm.trim()) return null;

    const results = [];
    const searchLower = searchTerm.toLowerCase();

    Object.entries(mergedKnowledgeBase).forEach(([categoryKey, category]) => {
      category.items.forEach(item => {
        const questionMatch = item.question.toLowerCase().includes(searchLower);
        const answerMatch = item.answer.toLowerCase().includes(searchLower);
        const keywordMatch = item.keywords.some(keyword => 
          keyword.toLowerCase().includes(searchLower)
        );

        if (questionMatch || answerMatch || keywordMatch) {
          results.push({
            ...item,
            category: category.title,
            categoryKey,
            categoryColor: category.color
          });
        }
      });
    });

    // Log the search query with results
    if (searchTerm.trim() !== '') {
      logQuery(searchTerm.trim(), results.length > 0);
    }

    return results;
  }, [searchTerm, knowledgeBase, logQuery]);

  // Handle feedback
  const handleFeedback = async (itemId, helpful) => {
    const feedbackType = helpful ? 'helpful' : 'not_helpful';
    
    setFeedbackGiven(prev => ({
      ...prev,
      [itemId]: helpful
    }));
    
    // Log feedback to backend if we have a current query ID
    if (currentQueryId) {
      await logFeedback(currentQueryId, feedbackType);
    }
  };

  // Render FAQ item
  const renderFAQItem = (item, showCategory = false) => (
    <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      {showCategory && (
        <div className={`text-sm font-medium ${item.categoryColor} mb-2`}>
          {item.category}
        </div>
      )}
      <h3 className="font-semibold text-gray-900 mb-2">{item.question}</h3>
      <div className="text-gray-700 mb-3 whitespace-pre-line">{item.answer}</div>
      
      {/* Feedback section */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="text-sm text-gray-500">Was this helpful?</div>
        <div className="flex space-x-2">
          {feedbackGiven[item.id] === undefined ? (
            <>
              <button
                onClick={() => handleFeedback(item.id, true)}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Yes
              </button>
              <button
                onClick={() => handleFeedback(item.id, false)}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                No
              </button>
            </>
          ) : (
            <div className={`text-sm ${feedbackGiven[item.id] ? 'text-green-600' : 'text-red-600'}`}>
              Thank you for your feedback!
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Support</h1>
          <p className="text-gray-600">Find answers to common questions and get help with system issues</p>
          
          {/* Commission Guidelines Warning */}
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
            <div className="flex items-start">
              <CreditCard className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-800 mb-1">Commission Issues?</h3>
                <p className="text-yellow-700 text-sm mb-2">
                  <strong>For commission corrections, updates, or questions:</strong> Use the messaging feature 
                  directly on your commission in the Commissions section.
                </p>
                <p className="text-yellow-700 text-sm mb-2">
                  <strong>Only use support tickets if:</strong> The commission system itself isn't working 
                  (errors, can't submit, page won't load, etc.)
                </p>
                <div className="mt-2">
                  <button 
                    onClick={() => window.location.href = '/agent/commissions'}
                    className="text-sm text-yellow-800 underline hover:text-yellow-900 transition-colors"
                  >
                    → Go to Commissions section
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* AI Assistant Toggle */}
          {aiStatus.isAvailable && (
            <div className="mt-4">
              <button
                onClick={() => setShowAIAssistant(!showAIAssistant)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Bot className="w-5 h-5 mr-2" />
                {showAIAssistant ? 'Hide AI Assistant' : 'Ask AI Assistant'}
              </button>
            </div>
          )}
        </div>

        {/* AI Assistant Interface */}
        {showAIAssistant && (
          <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Bot className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">AI Assistant</h2>
            </div>
            
            {/* AI Query Input */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ask me anything about Travel+ CRM..."
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !aiLoading && askAI(aiQuery)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={aiLoading}
                />
                <button
                  onClick={() => askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery.trim()}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {aiLoading ? 'Thinking...' : 'Ask'}
                </button>
              </div>
            </div>

            {/* AI Response */}
            {aiResponse && (
              <div className="bg-gray-50 rounded-lg p-4">
                {aiResponse.error ? (
                  <div className="text-red-600">
                    <p className="font-medium">Error: {aiResponse.message}</p>
                    {aiResponse.fallback && (
                      <p className="text-sm mt-2">{aiResponse.fallback}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 mb-2">You asked: "{aiResponse.query}"</p>
                      <div className="prose prose-sm max-w-none">
                        <p className="whitespace-pre-wrap">{aiResponse.response}</p>
                      </div>
                    </div>
                    
                    {/* Relevant Articles */}
                    {aiResponse.relevantArticles && aiResponse.relevantArticles.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Related Knowledge Base Articles:</p>
                        <div className="space-y-2">
                          {aiResponse.relevantArticles.map((article, index) => (
                            <div key={index} className="text-sm">
                              <span className="font-medium text-blue-600">{article.question}</span>
                              <span className="text-gray-500 ml-2">({article.category})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* AI Feedback */}
                    {aiResponse.conversationId && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Was this response helpful?</p>
                        <div className="flex gap-2">
                          {['excellent', 'good', 'fair', 'poor'].map((rating) => (
                            <button
                              key={rating}
                              onClick={() => submitAIFeedback(aiResponse.conversationId, rating)}
                              className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors capitalize"
                            >
                              {rating}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for help with errors, features, or common issues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Search Results */}
      {filteredResults && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Search Results ({filteredResults.length})
          </h2>
          {filteredResults.length > 0 ? (
            <div>
              {filteredResults.map(item => renderFAQItem(item, true))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No results found for "{searchTerm}"</p>
              <button
                onClick={() => setSearchTerm('')}
                className="text-blue-600 hover:text-blue-800"
              >
                Clear search and browse categories
              </button>
            </div>
          )}
        </div>
      )}

      {/* Categories (show when not searching) */}
      {!searchTerm.trim() && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Browse by Category</h2>
          <div className="space-y-4">
            {Object.entries(mergedKnowledgeBase).map(([categoryKey, category]) => {
              const Icon = category.icon;
              const isExpanded = expandedCategories[categoryKey];
              
              return (
                <div key={categoryKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(categoryKey)}
                    className={`w-full px-6 py-4 text-left ${category.bgColor} hover:opacity-80 transition-opacity flex items-center justify-between`}
                  >
                    <div className="flex items-center">
                      <Icon className={`w-5 h-5 ${category.color} mr-3`} />
                      <span className="font-semibold text-gray-900">{category.title}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({category.items.length} items)
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-6 bg-white">
                      {category.items.map(item => renderFAQItem(item))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contact Support */}
      <div className="mt-12 p-6 bg-gray-50 rounded-lg text-center">
        <h3 className="font-semibold text-gray-900 mb-2">Still need help?</h3>
        <p className="text-gray-600 mb-4">
          If you can't find the answer you're looking for, contact your agency administrator or support team.
        </p>
        <button 
          onClick={() => setShowContactForm(true)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Contact Support
        </button>
      </div>

      {/* Contact Support Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Contact Support</h3>
                <button
                  onClick={closeContactForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {contactFormSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Support Request Sent!</h4>
                  <p className="text-gray-600 mb-4">
                    Your message has been sent to our support team at support@travelpluscrm.com. 
                    You should receive a response within 24 hours.
                  </p>
                  <p className="text-sm text-gray-500">
                    This window will close automatically in a few seconds.
                  </p>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); submitContactForm(); }}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                        Priority Level
                      </label>
                      <select
                        id="priority"
                        value={contactForm.priority}
                        onChange={(e) => handleContactFormChange('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="low">Low - General question</option>
                        <option value="normal">Normal - Need assistance</option>
                        <option value="high">High - Urgent issue</option>
                        <option value="critical">Critical - System down</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        value={contactForm.subject}
                        onChange={(e) => handleContactFormChange('subject', e.target.value)}
                        placeholder="Brief description of your issue"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        value={contactForm.message}
                        onChange={(e) => handleContactFormChange('message', e.target.value)}
                        placeholder="Please provide detailed information about your issue, including any error messages, steps you've taken, and what you were trying to accomplish."
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                        required
                      />
                    </div>

                    {/* Commission keyword warning */}
                    {checkForCommissionKeywords(contactForm.subject, contactForm.message) && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start">
                          <CreditCard className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-red-800 font-medium mb-1">
                              Commission-related issue detected
                            </p>
                            <p className="text-sm text-red-700 mb-2">
                              For commission corrections or questions, please use the messaging feature 
                              directly on your commission instead of submitting a support ticket.
                            </p>
                            <p className="text-sm text-red-700 mb-2">
                              Only submit this ticket if the commission system itself isn't working.
                            </p>
                            <button 
                              type="button"
                              onClick={() => window.location.href = '/agent/commissions'}
                              className="text-sm text-red-800 underline hover:text-red-900 transition-colors"
                            >
                              → Go to Commissions section instead
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Your message will be sent to support@travelpluscrm.com. 
                        Please include your agent ID and agency information for faster assistance.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={closeContactForm}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      disabled={contactFormSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={contactFormSubmitting || !contactForm.subject.trim() || !contactForm.message.trim()}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {contactFormSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AgentSupport;
